import React, { useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { ReactComponent as CloseIcon } from '../../shared/icons/close-icon.svg';
import styles from './Slideout.module.scss';

interface SlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | ReactNode;
  width?: 'medium' | 'large';
  children: ReactNode;
  disableOutsideClick?: boolean;
}

const Slideout: React.FC<SlideoutProps> = ({ isOpen, onClose, title, width = 'medium', children, disableOutsideClick = false }) => {
  // Handle escape key - React 18 native event handling
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Use React 18 compatible event handling
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when slideout is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const slideoutContent = (
    <div
      className={styles.slideoutOverlay}
      // React 18 native click-outside detection - no document listeners!
      onPointerDown={(e) => {
        // Only close if clicking the overlay itself, not its children
        // Respect disableOutsideClick prop to prevent accidental closure
        if (e.target === e.currentTarget && !disableOutsideClick) {
          onClose();
        }
      }}
    >
      <div
        className={classNames(styles.slideout, styles[`slideout--${width}`])}
        // Prevent clicks inside slideout from bubbling to overlay
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div className={styles.slideout__header}>
            <h2 className={styles.slideout__title}>{title}</h2>
            <button className={styles.slideout__closeButton} onClick={onClose} aria-label="Close slideout">
              <CloseIcon />
            </button>
          </div>
        )}

        {/* Content */}
        <div className={styles.slideout__content}>{children}</div>
      </div>
    </div>
  );

  // Use React Portal to render outside main DOM tree
  return createPortal(slideoutContent, document.body);
};

export default Slideout;
